import React from 'react';
import { Row, Col, Card, Form, Spinner, Badge, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSave,
  faPaperPlane,
  faSearch,
  faSitemap,
  faCircleInfo,
  faClockRotateLeft,
  faCheckDouble,
  faCaretSquareLeft,
  faCaretSquareRight,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';

import ServerTable from '../../Components/Table/ServerTabe';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import { ConciliationApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import { formatCurrencyNum, formatDate } from '../../Utils/formaters';
import {
  mapConciliationRelRow,
  mapConciliationRelDetail,
  mapProcessRelResult,
  pickFirst,
  toBool
} from '../../Models/ConciliationRelModels';
import DynamicCostingCodeFields from './DynamicCostingCodeFields';
import IncorrectMatchControl from '../../Components/IncorrectMatch/IncorrectMatchControl';
import { openInvoicePdf } from './invoicePdf';
import { downloadConciliationCsv } from './exportConciliationCsv';
import {
  costingCodeOptionsByDimension,
  costingCodesFromDecision,
  firstDefined,
  normalizeCostingDimensions,
  withLegacyCostingFields
} from './costingCodeUtils';

const ActionBtn = ({ title, icon, onClick, disabled, variant = 'primary' }) => (
  <button className={`btn btn-sm btn-${variant} fw-bold me-2`} onClick={onClick} disabled={disabled}>
    <FontAwesomeIcon icon={icon} className="me-1" />
    {title}
  </button>
);

const statusBadge = (status) => {
  const s = (status ?? '').toLowerCase();
  if (s === 'succeeded' || s === 'success') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'pending') return 'warning';
  if (s === 'skipped') return 'secondary';
  if (s === 'needsworkbench' || s === 'needsdata') return 'warning';
  if (s === 'completed') return 'success';
  if (s === 'match') return 'success';
  return 'secondary';
};

const fieldRow = (label, value) => (
  <div className="ofnm-field-row">
    <div className="ofnm-field-label">{label}</div>
    <div className="ofnm-field-value">{value ?? <span className="text-muted">-</span>}</div>
  </div>
);

const findOption = (options, value) =>
  value ? options.find((o) => o.value === value) ?? null : null;

const normalizeDateInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const dateOnly = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : '';
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};

const getDecisionValue = (decision, camelKey) => {
  if (!decision) return null;
  const pascalKey = `${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
  return decision[camelKey] ?? decision[pascalKey] ?? null;
};

const priorityHelper = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 'No helper available for this priority code.';
  if (n >= 100 && n <= 130) return '100-130: match by date + amount + provider.';
  if (n >= 200 && n <= 230) return '200-230: match by date + amount.';
  if (n >= 0 && n <= 30) return '00-30: day distance between payment and invoice.';
  return 'Outside defined helper ranges.';
};

const isProcessedBoth = (item) => {
  const both = toBool(pickFirst(item, ['isProcessedBoth', 'IsProcessedBoth']));
  if (both) return true;
  const pi = toBool(pickFirst(item, ['isProcessedPurchaseInvoice', 'IsProcessedPurchaseInvoice']));
  const vp = toBool(pickFirst(item, ['isProcessedVendorPayment', 'IsProcessedVendorPayment']));
  return pi && vp;
};

const isIncorrectMatch = (item) =>
  toBool(pickFirst(item, ['isIncorrectMatch', 'IsIncorrectMatch']));

const renderStatsBadges = (stats) => {
  if (!stats || typeof stats !== 'object') return null;
  const entries = Object.entries(stats).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v));
  if (!entries.length) return null;
  return (
    <div className="d-flex flex-wrap gap-2">
      {entries.slice(0, 6).map(([key, value]) => (
        <Badge key={key} bg="light" text="dark" className="border">
          {key}: {String(value)}
        </Badge>
      ))}
    </div>
  );
};

const ConciliaRelWorkbenchTab = ({
  companyId,
  getToken,
  showInfoModal,
  externalRefreshTick = 0,
  onSuggestionsFilterChange
}) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [selectedKeys, setSelectedKeys] = React.useState(new Set());
  const [selectedKey, setSelectedKey] = React.useState(null);

  const [details, setDetails] = React.useState(null);
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [workflow, setWorkflow] = React.useState([]);

  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [loadingStats, setLoadingStats] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [completing, setCompleting] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [processingBatch, setProcessingBatch] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [loadingMasters, setLoadingMasters] = React.useState(false);

  const [vendorOptions, setVendorOptions] = React.useState([]);
  const [glOptions, setGlOptions] = React.useState([]);
  const [taOptions, setTaOptions] = React.useState([]);
  const [costingOptionsByDimension, setCostingOptionsByDimension] = React.useState({});
  const [costingDimensions, setCostingDimensions] = React.useState([]);

  const [quickSearch, setQuickSearch] = React.useState('');
  const [exporting, setExporting] = React.useState(false);
  const [isDetailsCollapsed, setIsDetailsCollapsed] = React.useState(true);
  const [tableQuery, setTableQuery] = React.useState({
    pageIndex: 0,
    pageSize: 20,
    sorting: [],
    columnFilters: [],
    globalFilter: ''
  });
  const [validationErrors, setValidationErrors] = React.useState({});

  const [form, setForm] = React.useState({
    vendorCardCode: null,
    accountCode: null,
    taxCode: null,
    costingCodes: {},
    docCurrency: 'QTZ',
    notes: '',
    transferAccount: null,
    transferSum: '',
    sumApplied: '',
    transferDate: '',
    transferReference: '',
    counterReference: '',
    remarks: '',
    journalRemarks: ''
  });
  const activeCompanyRef = React.useRef(companyId);
  const detailsRequestSeqRef = React.useRef(0);

  React.useEffect(() => {
    activeCompanyRef.current = companyId;
  }, [companyId]);

  React.useEffect(() => {
    detailsRequestSeqRef.current += 1;
    setSelectedKey(null);
    setDetails(null);
    setPdfUrl(null);
    setWorkflow([]);
    setSelectedKeys(new Set());
    setLoadingDetails(false);
    setLoadingStats(false);
    setLoadingMasters(false);
    setVendorOptions([]);
    setGlOptions([]);
    setTaOptions([]);
    setCostingOptionsByDimension({});
    setCostingDimensions([]);
  }, [companyId]);

  React.useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const requestCompanyId = companyId;

    (async () => {
      try {
        setLoadingMasters(true);
        const token = await getToken();
        const [vendors, gls, costingCodes] = await Promise.all([
          SapMasterApi.getVendors(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getGlAccounts(companyId, { search: '', page: 1, pageSize: 2000 }, token),
          SapMasterApi.getCostingCodes(companyId, { search: '', page: 1, pageSize: 5000 }, token)
        ]);

        if (cancelled || activeCompanyRef.current !== requestCompanyId) return;

        setVendorOptions((vendors.items ?? []).map((v) => ({
          value: v.cardCode,
          label: `${v.cardCode} - ${v.cardName ?? ''}`
        })));

        setGlOptions((gls.items ?? []).map((a) => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));

        setTaOptions((gls.items.filter(x => x.cashAccout == "Y") ?? []).map((a) => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));

        setCostingOptionsByDimension(costingCodeOptionsByDimension(costingCodes.items ?? []));
      } catch (e) {
        console.error('Failed loading CONCILIAREL masters', e);
      } finally {
        if (!cancelled && activeCompanyRef.current === requestCompanyId) setLoadingMasters(false);
      }
    })();

    return () => { cancelled = true; };
  }, [companyId, getToken]);

  const loadStats = React.useCallback(async () => {
    if (!companyId) return;
    const requestCompanyId = companyId;
    try {
      setLoadingStats(true);
      const token = await getToken();
      const data = await ConciliationApi.getConciliationRelStats({ companyId }, token);
      if (activeCompanyRef.current !== requestCompanyId) return;
      setStats(data ?? null);
    } catch (e) {
      console.error('Failed loading CONCILIAREL stats', e);
    } finally {
      if (activeCompanyRef.current === requestCompanyId) setLoadingStats(false);
    }
  }, [companyId, getToken]);

  React.useEffect(() => {
    loadStats();
  }, [loadStats, refreshTick]);

  const loadDetails = React.useCallback(async (conciliationKey) => {
    if (!conciliationKey) return;
    if (!activeCompanyRef.current) return;
    const requestId = detailsRequestSeqRef.current + 1;
    detailsRequestSeqRef.current = requestId;
    const requestCompanyId = activeCompanyRef.current;

    try {
      setLoadingDetails(true);
      setPdfUrl(null);
      const token = await getToken();

      const wbDto = await ConciliationApi.getConciliaRelWorkbench(conciliationKey, token);
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;
      const wbRow = wbDto?.row ?? wbDto?.Row ?? {};
      const wbDecision = wbDto?.decision ?? wbDto?.Decision ?? null;
      const wbActions = wbDto?.actions ?? wbDto?.Actions ?? [];

      const mapped = mapConciliationRelDetail({
        ...wbRow,
        decision: wbDecision
      });
      setDetails(mapped);
      setPdfUrl(wbDto?.pdfUrl ?? wbDto?.PdfUrl ?? null);
      setWorkflow(wbActions);

      const dec = mapped?.decision ?? {};
      const rawDimensions = firstDefined(wbDto, ['costingDimensions', 'CostingDimensions']);
      const initialCodes = costingCodesFromDecision(dec);
      const dimensions = normalizeCostingDimensions(rawDimensions, initialCodes);
      setCostingDimensions(dimensions);
      setForm({
        vendorCardCode: getDecisionValue(dec, 'vendorCardCode'),
        accountCode: getDecisionValue(dec, 'accountCode'),
        taxCode: getDecisionValue(dec, 'taxCode'),
        costingCodes: costingCodesFromDecision(dec, dimensions),
        docCurrency: getDecisionValue(dec, 'docCurrency') ?? 'QTZ',
        notes: getDecisionValue(dec, 'notes') ?? '',
        transferAccount: getDecisionValue(dec, 'transferAccount'),
        transferSum: getDecisionValue(dec, 'transferSum') ?? '',
        sumApplied: getDecisionValue(dec, 'sumApplied') ?? '',
        transferDate: normalizeDateInput(getDecisionValue(dec, 'transferDate')),
        transferReference: getDecisionValue(dec, 'transferReference') ?? '',
        counterReference: getDecisionValue(dec, 'counterReference') ?? '',
        remarks: getDecisionValue(dec, 'remarks') ?? '',
        journalRemarks: getDecisionValue(dec, 'journalRemarks') ?? ''
      });
      setValidationErrors({});
    } catch (e) {
      if (requestId !== detailsRequestSeqRef.current || activeCompanyRef.current !== requestCompanyId) return;
      console.error('Failed loading CONCILIAREL details', e);
      showInfoModal('Error', <p className="mb-0">Could not load workbench details.</p>);
    } finally {
      if (requestId === detailsRequestSeqRef.current && activeCompanyRef.current === requestCompanyId) {
        setLoadingDetails(false);
      }
    }
  }, [getToken, showInfoModal]);

  const buildRelFilter = React.useCallback(({ pageIndex = 0, pageSize = 20, globalFilter = '', sorting = [], columnFilters = [] } = {}) => {
    const sort = sorting?.[0];
    const sortByMap = {
      prioridadConcilia: 'PrioridadConcilia',
      pfFecha: 'PFFecha',
      pfMonto: 'PFMonto',
      pfNombreProveedor: 'PFNombreProveedor',
      pfNitProveedor: 'PFNitProveedor',
      goFecha: 'GOFecha',
      goMonto: 'GOMonto',
      goNombreProveedor: 'GONombreProveedor',
      goNitProveedor: 'GONitProveedor',
      goSerieID: 'GOSerieID',
      goNumeroID: 'GONumeroID',
      cuentaDescripcion: 'CuentaDescripcion',
      tipoCuenta: 'TipoCuenta',
      processed: 'IsProcessedBoth'
    };
    const sortBy = sortByMap[sort?.id] ?? 'PFFecha';
    const sortDir = sort ? (sort?.desc ? 'desc' : 'asc') : 'desc';
    const columnFilterMap = {
      prioridadConcilia: 'PrioridadConcilia',
      pfFecha: 'PFFecha',
      pfMonto: 'PFMonto',
      pfNombreProveedor: 'PFNombreProveedor',
      pfNitProveedor: 'PFNitProveedor',
      goFecha: 'GOFecha',
      goMonto: 'GOMonto',
      goNombreProveedor: 'GONombreProveedor',
      goNitProveedor: 'GONitProveedor',
      goSerieID: 'GOSerieID',
      goNumeroID: 'GONumeroID',
      cuentaDescripcion: 'CuentaDescripcion',
      tipoCuenta: 'tipoCuenta',
      processed: 'IsProcessedBoth'
    };
    const columnFilterParams = {};
    (columnFilters ?? []).forEach((f) => {
      const key = columnFilterMap[f?.id];
      const rawValue = (f?.value ?? '').toString().trim();
      if (f?.id === 'processed' && rawValue === 'Incorrect Match') {
        columnFilterParams.IsIncorrectMatch = 'true';
      } else {
        const value = f?.id === 'processed'
          ? (rawValue === 'Both' ? 'true' : rawValue === 'Pending' ? 'false' : '')
          : rawValue;
        if (key && value) columnFilterParams[key] = value;
      }
    });
    const search = globalFilter || quickSearch || '';

    return {
      companyId,
      page: pageIndex + 1,
      pageSize,
      search,
      sortBy,
      sortDir,
      ...columnFilterParams
    };
  }, [companyId, quickSearch]);

  const currentSuggestionsFilter = React.useMemo(
    () => buildRelFilter(tableQuery),
    [buildRelFilter, tableQuery]
  );

  React.useEffect(() => {
    onSuggestionsFilterChange?.(currentSuggestionsFilter);
  }, [currentSuggestionsFilter, onSuggestionsFilterChange]);

  const fetchRelRows = React.useCallback(async (query) => {
    if (!companyId) return { items: [], totalItems: 0 };
    const requestCompanyId = companyId;
    const token = await getToken();
    const res = await ConciliationApi.getConciliationRel(buildRelFilter(query), token);
    if (activeCompanyRef.current !== requestCompanyId) {
      return { items: [], totalItems: 0 };
    }

    const items = (res.items ?? []).map(mapConciliationRelRow);
    setRows(items);
    return { items, totalItems: res.totalCount ?? res.totalItems ?? 0 };
  }, [buildRelFilter, companyId, externalRefreshTick, getToken, refreshTick]);

  const onSelectRow = async (row) => {
    const key = row.conciliationKey ?? row.ConciliationKey;
    setIsDetailsCollapsed(false);
    setSelectedKey(key);
    await loadDetails(key);
  };

  const validateDecision = React.useCallback(() => {
    const errors = {};
    if (!form.transferAccount) {
      errors.transferAccount = 'TransferAccount is required for vendor payment.';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form.transferAccount]);

  const saveDecision = async () => {
    if (!selectedKey) return;
    if (isProcessedBoth(details) || isIncorrectMatch(details)) return;
    if (!validateDecision()) return;

    try {
      setSaving(true);
      const token = await getToken();
      await ConciliationApi.saveConciliaRelDecision(selectedKey, withLegacyCostingFields(form), token);
      await loadDetails(selectedKey);
      showInfoModal('Saved', <p className="mb-0">Decision saved successfully.</p>);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      console.error('Save decision failed', e);
      showInfoModal('Error', <p className="mb-0">Could not save decision.</p>);
    } finally {
      setSaving(false);
    }
  };

  const completeDecision = async () => {
    if (!selectedKey) return;
    if (isProcessedBoth(details) || isIncorrectMatch(details)) return;
    if (!validateDecision()) return;

    try {
      setCompleting(true);
      const token = await getToken();
      await ConciliationApi.completeConciliaRelDecision(selectedKey, withLegacyCostingFields(form), token);
      await loadDetails(selectedKey);
      showInfoModal('Completed', <p className="mb-0">Decision completed successfully.</p>);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      console.error('Complete decision failed', e);
      showInfoModal('Error', <p className="mb-0">Could not complete decision.</p>);
    } finally {
      setCompleting(false);
    }
  };

  const processRecord = async () => {
    if (!selectedKey) return;
    if (isProcessedBoth(details) || isIncorrectMatch(details)) return;

    try {
      setProcessing(true);
      const token = await getToken();
      const raw = await ConciliationApi.processConciliationRelRecord(selectedKey, token);
      const res = mapProcessRelResult(raw);

      await loadDetails(selectedKey);
      setRefreshTick((t) => t + 1);

      showInfoModal(
        'Process result',
        <div>
          <p className="mb-2"><strong>Status:</strong> {res.status}</p>
          <p className="mb-2"><strong>Created Purchase Invoice DocEntry:</strong> {res.createdSapDocEntry ?? '-'}</p>
          <p className="mb-2"><strong>Created Vendor Payment DocEntry:</strong> {res.createdSapVendorPaymentDocEntry ?? '-'}</p>
          {(res.messages ?? []).length > 0 && (
            <ul className="mb-0">
              {(res.messages ?? []).map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
        </div>
      );
    } catch (e) {
      console.error('Process REL failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process record.</p>);
    } finally {
      setProcessing(false);
    }
  };

  const processSelected = async () => {
    const keys = rows
      .filter((row) => selectedKeys.has(row.conciliationKey) && !isProcessedBoth(row) && !isIncorrectMatch(row))
      .map((row) => row.conciliationKey);
    if (!keys.length) return;

    try {
      setProcessingBatch(true);
      showInfoModal(
        'Processing started',
        <p className="mb-0">
          The selected records are being processed now. This may take a moment depending on how many were selected.
        </p>
      );

      const token = await getToken();
      const res = await ConciliationApi.processConciliationRelBatch(keys, token);
      setSelectedKeys(new Set());
      setRefreshTick((t) => t + 1);
      if (selectedKey) await loadDetails(selectedKey);

      showInfoModal(
        'Batch processing finished',
        <div>
          <p className="mb-2">
            Completed processing for <strong>{res.total}</strong> record(s).
          </p>
          <ul className="mb-2">
            <li>Succeeded: {res.succeeded}</li>
            <li>Failed: {res.failed}</li>
          </ul>
          {(res.items ?? []).some((x) => !x.success) && (
            <>
              <strong>Failures:</strong>
              <ul className="mb-0 mt-1">
                {res.items
                  .filter((x) => !x.success)
                  .slice(0, 10)
                  .map((x, i) => (
                    <li key={i}>
                      {x.conciliationKey} - {x.error ?? x.status}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      );
    } catch (e) {
      console.error('REL batch processing failed', e);
      showInfoModal('Error', <p className="mb-0">Could not process selected records.</p>);
    } finally {
      setProcessingBatch(false);
    }
  };

  const openProcessSelectedConfirm = () => {
    if (selectedKeys.size === 0) return;
    setConfirmAction('processBatch');
  };

  const closeConfirmModal = () => setConfirmAction(null);

  const onConfirmBatchAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'processBatch') await processSelected();
  };

  const selectableRows = React.useMemo(
    () => rows.filter((r) => !isProcessedBoth(r) && !isIncorrectMatch(r)),
    [rows]
  );

  const handleIncorrectMatchMarked = React.useCallback(async (_result, conciliationKey) => {
    setRows((current) => current.map((row) =>
      row.conciliationKey === conciliationKey ? { ...row, isIncorrectMatch: true } : row
    ));
    setDetails((current) => (current?.conciliationKey ?? selectedKey) === conciliationKey
      ? { ...current, isIncorrectMatch: true }
      : current);
    setRefreshTick((tick) => tick + 1);
    setSelectedKeys((current) => {
      const next = new Set(current);
      next.delete(conciliationKey);
      return next;
    });
    if (selectedKey === conciliationKey) {
      await loadDetails(conciliationKey);
      setDetails((current) => ({ ...current, isIncorrectMatch: true }));
    }
  }, [loadDetails, selectedKey]);

  const columns = React.useMemo(() => [
    {
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={selectableRows.length > 0 && selectableRows.every((r) => selectedKeys.has(r.conciliationKey))}
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) selectableRows.forEach((r) => next.add(r.conciliationKey));
            else selectableRows.forEach((r) => next.delete(r.conciliationKey));
            setSelectedKeys(next);
          }}
        />
      ),
      cell: ({ row }) => (
        <Form.Check
          type="checkbox"
          checked={selectedKeys.has(row.original.conciliationKey)}
          disabled={isProcessedBoth(row.original) || isIncorrectMatch(row.original)}
          onChange={(e) => {
            const next = new Set(selectedKeys);
            if (e.target.checked) next.add(row.original.conciliationKey);
            else next.delete(row.original.conciliationKey);
            setSelectedKeys(next);
          }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false
    },
    // {
    //   header: 'Status',
    //   accessorKey: 'estadoConcilia',
    //   cell: ({ row }) => <Badge bg={statusBadge(row.original.estadoConcilia)}>{row.original.estadoConcilia}</Badge>
    // },
    // {
    //   header: 'Decision',
    //   id: 'decisionStatus',
    //   enableSorting: false,
    //   enableColumnFilter: false,
    //   cell: ({ row }) => (
    //     row.original.hasCompletedDecision
    //       ? <Badge bg="success">Completed</Badge>
    //       : row.original.hasDecision
    //         ? <Badge bg="warning" text="dark">Draft</Badge>
    //         : <Badge bg="secondary">None</Badge>
    //   )
    // },
    {
      header: 'Processed',
      id: 'processed',
      accessorFn: (row) => isIncorrectMatch(row) ? 'Incorrect Match' : isProcessedBoth(row) ? 'Both' : 'Pending',
      meta: { filterVariant: 'select', filterOptions: ['Both', 'Pending', 'Incorrect Match'] },
      cell: ({ getValue }) => (
        getValue() === 'Incorrect Match'
          ? <Badge bg="danger">Incorrect match</Badge>
          : getValue() === 'Both'
            ? <Badge bg="success">Both</Badge>
            : <Badge bg="secondary">Pending</Badge>
      )
    },
    { header: 'Priority', accessorKey: 'prioridadConcilia', cell: (info) => <div className="text-end">{info.getValue()}</div> },
    { header: 'Inv Date', accessorKey: 'goFecha', cell: (info) => formatDate(info.getValue()) },
    { header: 'Inv Amount', accessorKey: 'goMonto', cell: (info) => <div className="text-end">{formatCurrencyNum(info.getValue())}</div> },
    { header: 'Inv Provider', accessorKey: 'goNombreProveedor' },
    { header: 'Inv NIT', accessorKey: 'goNitProveedor' },
    { header: 'Inv Serie', accessorKey: 'goSerieID' },
    { header: 'Inv Number', accessorKey: 'goNumeroID' },
    { header: 'Trx Account', accessorKey: 'cuentaDescripcion'},
    { header: 'Acc Type', id: 'tipoCuenta', accessorFn: (row) => row.tipoCuenta ?? row.TipoCuenta ?? '' },
    { header: 'Trx Date', accessorKey: 'pfFecha', cell: (info) => formatDate(info.getValue()) },
    { header: 'Trx Amount', accessorKey: 'pfMonto', cell: (info) => <div className="text-end">{formatCurrencyNum(info.getValue())}</div> },
    { header: 'Trx Provider', accessorKey: 'pfNombreProveedor' },
    { header: 'Trx NIT', accessorKey: 'pfNitProveedor' },
  ], [selectableRows, selectedKeys]);

  const processed = isProcessedBoth(details);
  const incorrectMatch = isIncorrectMatch(details);
  const detailsLocked = processed || incorrectMatch;
  const status = pickFirst(details, ['estadoConcilia', 'EstadoConcilia']);
  const priority = pickFirst(details, ['prioridadConcilia', 'PrioridadConcilia']);

  return (
    <div className={`ofnm-tab-shell collapsible-details-shell${isDetailsCollapsed ? ' details-collapsed' : ''}`}>
      <div className="ofnm-pane ofnm-left">
        <Card className="h-100 shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="fw-bold">
              <FontAwesomeIcon icon={faSitemap} className="me-2" />
              Full match SAT + BAC/CONCUR (ConciliaRel)
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {loadingStats ? <Spinner animation="border" size="sm" /> : renderStatsBadges(stats)}

              <InputGroup size="sm" style={{ width: 260 }}>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search provider, NIT, amount..."
                  value={quickSearch}
                  onChange={(e) => setQuickSearch(e.target.value)}
                />
              </InputGroup>

              <button className="btn btn-sm btn-outline-success" disabled={!companyId || exporting}
                onClick={async () => {
                  try {
                    setExporting(true);
                    await downloadConciliationCsv('rel', currentSuggestionsFilter, getToken);
                  } catch (e) {
                    console.error('Rel CSV export failed', e);
                    showInfoModal('Error', <p className="mb-0">Could not download CSV.</p>);
                  } finally { setExporting(false); }
                }}>
                {exporting ? 'Downloading...' : 'Download CSV'}
              </button>

              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setRefreshTick((t) => t + 1)}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>

              <button
                className="btn btn-sm btn-warning"
                onClick={openProcessSelectedConfirm}
                disabled={selectedKeys.size === 0 || processingBatch}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                Process Selected
              </button>
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body p-0">
            <div className="ofnm-table-region">
              <ServerTable
                columns={columns}
                fetchData={fetchRelRows}
                tableClass="table-hover text-nowrap no-select"
                containerClass="table-responsive"
                headerClass="table-light text-center"
                enableGlobalFilter={false}
                initialPageSize={20}
                onQueryStateChange={setTableQuery}
                onRowDoubleClick={onSelectRow}
                getRowClassName={(row) => (selectedKey === row.conciliationKey ? 'table-primary' : '')}
                fillAvailableHeight
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      <div
        id="conciliarel-details-panel"
        className="ofnm-pane ofnm-right workbench-details-pane"
      >
        <Card className="h-100 shadow-sm position-relative">
          {processing && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white"
              style={{ zIndex: 10, opacity: 0.9 }}
            >
              <Spinner animation="border" className="mb-3" />
              <div className="fw-semibold">Processing in background...</div>
              <div className="text-muted small">Please wait while SAP posting completes.</div>
            </div>
          )}
          <Card.Header className="d-flex align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary workbench-details-toggle"
              onClick={() => setIsDetailsCollapsed((collapsed) => !collapsed)}
              title={isDetailsCollapsed ? 'Show ConciliaRel Details' : 'Hide ConciliaRel Details'}
              aria-label={isDetailsCollapsed ? 'Show ConciliaRel Details' : 'Hide ConciliaRel Details'}
              aria-expanded={!isDetailsCollapsed}
              aria-controls="conciliarel-details-panel"
            >
              <FontAwesomeIcon icon={isDetailsCollapsed ? faCaretSquareLeft : faCaretSquareRight} />
            </button>

            <div className="fw-bold workbench-details-title me-auto">
              <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
              ConciliaRel Details
            </div>

            <div className="d-flex align-items-center flex-wrap gap-2 workbench-details-actions">
              {pdfUrl && (
                <ActionBtn
                  title="View invoice"
                  icon={faFilePdf}
                  variant="outline-primary"
                  onClick={() => openInvoicePdf(pdfUrl)}
                />
              )}
              <ActionBtn
                title="Refresh"
                icon={faRefresh}
                variant="outline-primary"
                onClick={() => selectedKey && loadDetails(selectedKey)}
                disabled={!selectedKey || loadingDetails}
              />
              <ActionBtn
                title="Save"
                icon={faSave}
                onClick={saveDecision}
                disabled={!selectedKey || saving || detailsLocked}
              />
              <ActionBtn
                title="Process"
                icon={faPaperPlane}
                variant="warning"
                onClick={processRecord}
                disabled={!selectedKey || processing || detailsLocked}
              />
              {details && (
                <IncorrectMatchControl
                  row={{ ...details, conciliationKey: details.conciliationKey ?? selectedKey }}
                  conciliationType="rel"
                  getToken={getToken}
                  onMarked={handleIncorrectMatchMarked}
                  buttonLabel="Mark association as incorrect"
                  buttonClassName="btn btn-sm btn-outline-danger fw-bold me-2"
                />
              )}
            </div>
          </Card.Header>

          <Card.Body className="ofnm-card-body">
            <div className="ofnm-scroll ofnm-details-scroll">
              {!selectedKey && (
                <div className="text-muted">
                  Double-click a row from the table to open the workbench.
                </div>
              )}

              {loadingDetails && (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" />
                </div>
              )}

              {!loadingDetails && details && (
                <>
                  {processed && (
                    <div className="alert alert-success mb-3">
                      This record is fully processed. Purchase Invoice and Vendor Payment were already created.
                    </div>
                  )}
                  {incorrectMatch && (
                    <div className="alert alert-danger mb-3">
                      This record is marked as an incorrect match. The decision is read-only.
                    </div>
                  )}

                  <Card className="shadow-sm mb-3">
                    <Card.Header className="fw-bold">Match Quality Feedback</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={12}>
                          {fieldRow('PF Provider', pickFirst(details, ['pfNombreProveedor', 'PFNombreProveedor']))}
                        </Col>
                        <Col md={12}>
                          {fieldRow('PF NIT', pickFirst(details, ['pfNitProveedor', 'PFNitProveedor']))}
                        </Col>
                        <Col md={12}>
                          {fieldRow('GO Provider', pickFirst(details, ['goNombreProveedor', 'GONombreProveedor']))}
                        </Col>
                        <Col md={12}>
                          {fieldRow('GO NIT', pickFirst(details, ['goNitProveedor', 'GONitProveedor']))}
                        </Col>
                      </Row>
                      <div className="alert alert-info mt-2 mb-0 py-2">
                        <strong>Priority {priority ?? '-'}</strong>: {priorityHelper(priority)}
                      </div>
                    </Card.Body>
                  </Card>

                  <div className="ofnm-summary-grid">
                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">Transaction Information</Card.Header>
                      <Card.Body>
                        {/* {fieldRow('PFEmpresaID', pickFirst(details, ['pfEmpresaID', 'PFEmpresaID']))}
                        {fieldRow('PFEntidadFinancieraID', pickFirst(details, ['pfEntidadFinancieraID', 'PFEntidadFinancieraID']))}
                        {fieldRow('PFCuentaID', pickFirst(details, ['pfCuentaID', 'PFCuentaID']))} */}
                        {fieldRow('Transaction Number', pickFirst(details, ['pfNumeroTransaccion', 'PFNumeroTransaccion']))}
                        {fieldRow('Transaction Date', formatDate(pickFirst(details, ['pfFecha', 'PFFecha'])))}
                        {fieldRow('Transaction Amount', formatCurrencyNum(pickFirst(details, ['pfMonto', 'PFMonto'])))}
                        {fieldRow('Vendor Name', pickFirst(details, ['pfNombreProveedor', 'PFNombreProveedor']))}
                        {fieldRow('Vendor NIT', pickFirst(details, ['pfNitProveedor', 'PFNitProveedor']))}
                        {fieldRow('Transaction Key', pickFirst(details, ['pfLlave', 'PFLlave']))}
                      </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                      <Card.Header className="fw-bold">Official Invoice Information</Card.Header>
                      <Card.Body>
                        {/* {fieldRow('GOEmpresaID', pickFirst(details, ['goEmpresaID', 'GOEmpresaID']))}
                        {fieldRow('GOSerieID', pickFirst(details, ['goSerieID', 'GOSerieID']))}
                        {fieldRow('GONumeroID', pickFirst(details, ['goNumeroID', 'GONumeroID']))}
                        {fieldRow('GOFecha', formatDate(pickFirst(details, ['goFecha', 'GOFecha'])))}
                        {fieldRow('GOMonto', formatCurrencyNum(pickFirst(details, ['goMonto', 'GOMonto'])))} */}
                        {fieldRow('Vendor Name', pickFirst(details, ['goNombreProveedor', 'GONombreProveedor']))}
                        {fieldRow('Vendor NIT', pickFirst(details, ['goNitProveedor', 'GONitProveedor']))}
                        {/* {fieldRow('GOLlave', pickFirst(details, ['goLlave', 'GOLlave']))} */}
                        {fieldRow('Authorization Number', pickFirst(details, ['satNumeroAutorizacion', 'SatNumeroAutorizacion']))}
                        {fieldRow('Series', pickFirst(details, ['satSerie', 'SatSerie']))}
                        {fieldRow('DTE Number', pickFirst(details, ['satNumeroDte', 'SatNumeroDte']))}
                        {fieldRow('Issue Date', formatDate(pickFirst(details, ['satFechaEmision', 'SatFechaEmision'])))}
                        {fieldRow('Total Amount', formatCurrencyNum(pickFirst(details, ['satGranTotal', 'SatGranTotal'])))}
                        {fieldRow('Currency', pickFirst(details, ['satMoneda', 'SatMoneda']))}
                      </Card.Body>
                    </Card>
                  </div>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">Additional Information</Card.Header>
                    <Card.Body>
                      {fieldRow('Priority', priority)}
                      {fieldRow('Conciliation Status', <Badge bg={statusBadge(status)}>{status}</Badge>)}
                      {fieldRow('Company', pickFirst(details, ['empresaDescripcion', 'EmpresaDescripcion']))}
                      {fieldRow('Financial Entity', pickFirst(details, ['entidadFinancieraDescripcion', 'EntidadFinancieraDescripcion']))}
                      {fieldRow('Account Description', pickFirst(details, ['cuentaDescripcion', 'CuentaDescripcion']))}
                      {fieldRow('Account Type', pickFirst(details, ['tipoCuenta', 'TipoCuenta']))}
                      {fieldRow('Account Number', pickFirst(details, ['sourceOriginAccountNumber', 'SourceOriginAccountNumber']))}
                      {fieldRow('Source Type', pickFirst(details, ['sourceType', 'SourceType']))}
                      {fieldRow('Bank Statement ID', pickFirst(details, ['sourceBankStatementId', 'SourceBankStatementId']))}
                      {fieldRow('Bank Entry ID', pickFirst(details, ['sourceBankEntryId', 'SourceBankEntryId']))}
                      {fieldRow('Entry Reference', pickFirst(details, ['sourceEntryReference', 'SourceEntryReference']))}
                      {fieldRow('Amount', formatCurrencyNum(pickFirst(details, ['sourceAmount', 'SourceAmount'])))}
                      {fieldRow('Booking Date', formatDate(pickFirst(details, ['sourceBookingDate', 'SourceBookingDate'])))}
                      {fieldRow('Currency', pickFirst(details, ['sourceCurrencyEntry', 'SourceCurrencyEntry']))}
                      {fieldRow('Expense ID', pickFirst(details, ['sourceExpenseId', 'SourceExpenseId']))}
                      {fieldRow('Report ID', pickFirst(details, ['sourceReportId', 'SourceReportId']))}
                      {fieldRow('Vendor Name', pickFirst(details, ['sourceVendorName', 'SourceVendorName']))}
                      {fieldRow('Transaction Date', formatDate(pickFirst(details, ['sourceTransactionDate', 'SourceTransactionDate'])))}
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">SAP Entries</Card.Header>
                    <Card.Body>
                      <h6 className="fw-bold text-muted mb-2">Purchase Invoice</h6>
                      {fieldRow('Doc Entry', pickFirst(details, ['sapPurchaseInvoiceDocEntry', 'SapPurchaseInvoiceDocEntry']))}
                      {fieldRow('Document Number', pickFirst(details, ['sapPurchaseInvoiceDocNum', 'SapPurchaseInvoiceDocNum']))}
                      {fieldRow('Document Date', formatDate(pickFirst(details, ['sapPurchaseInvoiceDocDate', 'SapPurchaseInvoiceDocDate'])))}
                      {fieldRow('Vendor Code', pickFirst(details, ['sapPurchaseInvoiceCardCode', 'SapPurchaseInvoiceCardCode']))}
                      {fieldRow('Vendor Name', pickFirst(details, ['sapPurchaseInvoiceCardName', 'SapPurchaseInvoiceCardName']))}
                      {fieldRow('Document Total', formatCurrencyNum(pickFirst(details, ['sapPurchaseInvoiceDocTotal', 'SapPurchaseInvoiceDocTotal'])))}
                      {fieldRow('Currency', pickFirst(details, ['sapPurchaseInvoiceCurrency', 'SapPurchaseInvoiceCurrency']))}

                      <h6 className="fw-bold text-muted mb-2 mt-3">Vendor Payment</h6>
                      {fieldRow('Doc Entry', pickFirst(details, ['sapVendorPaymentDocEntry', 'SapVendorPaymentDocEntry']))}
                      {fieldRow('Document Number', pickFirst(details, ['sapVendorPaymentDocNum', 'SapVendorPaymentDocNum']))}
                      {fieldRow('Document Date', formatDate(pickFirst(details, ['sapVendorPaymentDocDate', 'SapVendorPaymentDocDate'])))}
                      {fieldRow('Vendor Code', pickFirst(details, ['sapVendorPaymentCardCode', 'SapVendorPaymentCardCode']))}
                      {fieldRow('Vendor Name', pickFirst(details, ['sapVendorPaymentCardName', 'SapVendorPaymentCardName']))}
                      {fieldRow('Transfer Amount', formatCurrencyNum(pickFirst(details, ['sapVendorPaymentTransferSum', 'SapVendorPaymentTransferSum'])))}
                      {fieldRow('Currency', pickFirst(details, ['sapVendorPaymentCurrency', 'SapVendorPaymentCurrency']))}
                      {fieldRow('Transfer Reference', pickFirst(details, ['sapVendorPaymentTransferReference', 'SapVendorPaymentTransferReference']))}

                      <h6 className="fw-bold text-muted mb-2 mt-3">Manual Processing</h6>
                      {fieldRow('Has Decision', toBool(pickFirst(details, ['hasDecision', 'HasDecision'])) ? <Badge bg="warning" text="dark">Draft</Badge> : <Badge bg="secondary">No</Badge>)}
                      {/* {fieldRow('Decision Completed', toBool(pickFirst(details, ['hasCompletedDecision', 'HasCompletedDecision'])) ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)} */}
                      {fieldRow('Purchase Invoice Processed', toBool(pickFirst(details, ['isProcessedPurchaseInvoice', 'IsProcessedPurchaseInvoice'])) ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)}
                      {fieldRow('Vendor Payment Processed', toBool(pickFirst(details, ['isProcessedVendorPayment', 'IsProcessedVendorPayment'])) ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)}
                      {fieldRow('Fully Processed', processed ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>)}
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">Decision</Card.Header>
                    <Card.Body>
                      <Card className="shadow-sm mb-3">
                        <Card.Header className="fw-bold">Purchase Invoice Fields</Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={12} className="mb-3">
                              <Form.Label>Vendor</Form.Label>
                              <Select
                                options={vendorOptions}
                                value={findOption(vendorOptions, form.vendorCardCode)}
                                onChange={(opt) => setForm((prev) => ({ ...prev, vendorCardCode: opt?.value ?? null }))}
                                isClearable
                                isDisabled={loadingMasters || detailsLocked}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                              />
                            </Col>
                            <Col md={12} className="mb-3">
                              <Form.Label>GL Account</Form.Label>
                              <Select
                                options={glOptions}
                                value={findOption(glOptions, form.accountCode)}
                                onChange={(opt) => setForm((prev) => ({ ...prev, accountCode: opt?.value ?? null }))}
                                isClearable
                                isDisabled={loadingMasters || detailsLocked}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                              />
                            </Col>
                            <Col md={6} className="mb-3">
                              <Form.Label>Tax Code</Form.Label>
                              <Form.Select
                                value={form.taxCode ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, taxCode: e.target.value }))}
                                disabled={detailsLocked}
                              >
                                <option value="IVA">IVA</option>
                                <option value="EXE">EXE</option>
                              </Form.Select>
                            </Col>
                            <Col md={6} className="mb-3">
                              <Form.Label>Currency</Form.Label>
                              <Form.Control
                                value={form.docCurrency ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, docCurrency: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col>
                            <DynamicCostingCodeFields
                              dimensions={costingDimensions}
                              values={form.costingCodes}
                              onChange={(costingCodes) => setForm((prev) => ({ ...prev, costingCodes }))}
                              optionMap={costingOptionsByDimension}
                              loading={loadingMasters}
                              disabled={detailsLocked}
                            />
                          </Row>
                        </Card.Body>
                      </Card>

                      <Card className="shadow-sm">
                        <Card.Header className="fw-bold">Vendor Payment Fields</Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={12} className="mb-1">
                              <Form.Label>Transfer Account</Form.Label>
                              <Select
                                options={taOptions}
                                value={findOption(taOptions, form.transferAccount)}
                                onChange={(opt) => {
                                  setForm((prev) => ({ ...prev, transferAccount: opt?.value ?? null }));
                                  setValidationErrors((prev) => ({ ...prev, transferAccount: null }));
                                }}
                                isClearable
                                isDisabled={loadingMasters || detailsLocked}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  control: (base) => ({
                                    ...base,
                                    borderColor: validationErrors.transferAccount ? '#dc3545' : base.borderColor
                                  })
                                }}
                              />
                            </Col>
                            {validationErrors.transferAccount && (
                              <Col md={12} className="mb-3">
                                <div className="text-danger small">{validationErrors.transferAccount}</div>
                              </Col>
                            )}
                            {/* <Col md={6} className="mb-3">
                              <Form.Label>Transfer Sum</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={form.transferSum ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, transferSum: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col>
                            <Col md={6} className="mb-3">
                              <Form.Label>Sum Applied</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={form.sumApplied ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, sumApplied: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col> */}
                            {/* <Col md={6} className="mb-3">
                              <Form.Label>Transfer Date</Form.Label>
                              <Form.Control
                                type="date"
                                value={form.transferDate ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, transferDate: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col> */}
                            {/* <Col md={12} className="mb-3">
                              <Form.Label>Transfer Reference</Form.Label>
                              <Form.Control
                                value={form.transferReference ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, transferReference: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col>
                            <Col md={12} className="mb-3">
                              <Form.Label>Counter Reference</Form.Label>
                              <Form.Control
                                value={form.counterReference ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, counterReference: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col> */}
                            <Col md={12} className="mb-3">
                              <Form.Label>Remarks</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={form.remarks ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col>
                            {/* <Col md={12} className="mb-2">
                              <Form.Label>Journal Remarks</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={form.journalRemarks ?? ''}
                                onChange={(e) => setForm((prev) => ({ ...prev, journalRemarks: e.target.value }))}
                                disabled={detailsLocked}
                              />
                            </Col> */}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Card.Body>
                  </Card>

                  <Card className="shadow-sm mt-3">
                    <Card.Header className="fw-bold">
                      <FontAwesomeIcon icon={faClockRotateLeft} className="me-2" />
                      Actions History
                    </Card.Header>
                    <Card.Body>
                      {(workflow ?? []).length === 0 && (
                        <div className="text-muted">No actions yet.</div>
                      )}

                      {(workflow ?? []).map((a, idx) => (
                        <div key={a.actionId ?? a.ActionId ?? idx} className="border rounded p-2 mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>{a.actionType ?? a.ActionType}</strong>
                            <Badge bg={statusBadge(a.status ?? a.Status)}>{a.status ?? a.Status}</Badge>
                          </div>
                          <div className="small text-muted mt-1">
                            Attempts: {a.attemptCount ?? a.AttemptCount} {(a.lastAttemptAtUtc ?? a.LastAttemptAtUtc) ? `| Last: ${a.lastAttemptAtUtc ?? a.LastAttemptAtUtc}` : ''}
                          </div>
                          {(a.externalRef1 ?? a.ExternalRef1) && <div className="small mt-1">Ref1: {a.externalRef1 ?? a.ExternalRef1}</div>}
                          {(a.externalRef2 ?? a.ExternalRef2) && <div className="small">Ref2: {a.externalRef2 ?? a.ExternalRef2}</div>}
                          {(a.error ?? a.Error) && <div className="small text-danger mt-1">{a.error ?? a.Error}</div>}
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <ConfirmationModal
        show={!!confirmAction}
        onHide={closeConfirmModal}
        title="Confirm Process Selected"
        content={(
          <div>
            <p className="mb-2">
              <>You are about to process <strong>{selectedKeys.size}</strong> selected record(s).</>
            </p>
            <p className="mb-0 text-muted">
              This action will create both Purchase Invoice and Vendor Payment for each selected row.
            </p>
          </div>
        )}
        onAcceptClick={onConfirmBatchAction}
        onCancelClick={closeConfirmModal}
      />
    </div>
  );
};

export default ConciliaRelWorkbenchTab;
